/**
 * Microcourse Studio — Element 渲染组件
 * 根据 MicroElement 类型渲染对应的 React 组件
 */

import React, { useState, useMemo } from 'react'
import type {
  MicroElement,
  HeadingElement,
  ParagraphElement,
  QuoteElement,
  ListElement,
  CodeElement,
  MathElement,
  ImageElement,
  VideoElement,
  AudioElement,
  IframeElement,
  TableElement,
  DividerElement,
  SpacerElement,
  GeometryElement,
  SvgExplainElement,
  SvgExplainScene,
  JsxgraphExplainElement,
  JsxgraphExplainScene,
  GeogebraExplainElement,
  GeogebraExplainScene,
  QuizElement,
  QuizContent,
  ElementStyle,
  ExcalidrawElement,
} from '@ai-tutor/shared'
import { normalizeQuizContent } from '@ai-tutor/shared'
import { LatexText } from '@/components/ui/LatexText'
import { ExcalidrawRenderer } from './ExcalidrawRenderer'

// ─── Style Helper ──────────────────────────────────────────────────

function styleToCSS(style?: ElementStyle): React.CSSProperties {
  if (!style) return {}
  const css: React.CSSProperties = {}
  if (style.align) css.textAlign = style.align
  if (style.fontSize) css.fontSize = style.fontSize
  if (style.textColor) css.color = style.textColor
  if (style.bgColor) css.backgroundColor = style.bgColor
  if (style.padding) css.padding = style.padding
  if (style.borderRadius) css.borderRadius = style.borderRadius
  if (style.marginTop) css.marginTop = style.marginTop
  if (style.marginBottom) css.marginBottom = style.marginBottom
  return css
}

// ─── Individual Element Renderers ──────────────────────────────────

function HeadingRenderer({ element }: { element: HeadingElement }) {
  const Tag = `h${element.level ?? 2}` as keyof JSX.IntrinsicElements
  return (
    <Tag style={styleToCSS(element.style)} className="font-bold mb-3">
      <LatexText>{element.content}</LatexText>
    </Tag>
  )
}

function ParagraphRenderer({ element }: { element: ParagraphElement }) {
  return (
    <div style={styleToCSS(element.style)} className="leading-relaxed mb-3 text-sm">
      <LatexText>{element.content}</LatexText>
    </div>
  )
}

function QuoteRenderer({ element }: { element: QuoteElement }) {
  return (
    <blockquote
      style={styleToCSS(element.style)}
      className="border-l-4 border-blue-400 pl-4 italic text-slate-600 mb-3"
    >
      <LatexText>{element.content}</LatexText>
      {element.citation && (
        <cite className="block text-xs mt-1 text-slate-400">— {element.citation}</cite>
      )}
    </blockquote>
  )
}

function ListRenderer({ element }: { element: ListElement }) {
  return (
    <div
      style={styleToCSS(element.style)}
      className="mb-3 text-sm [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:mb-1"
      dangerouslySetInnerHTML={{ __html: element.content }}
    />
  )
}

function CodeRenderer({ element }: { element: CodeElement }) {
  return (
    <pre
      style={styleToCSS(element.style)}
      className="bg-slate-900 text-slate-100 rounded-lg p-4 mb-3 overflow-x-auto text-xs font-mono"
    >
      <code>{element.content}</code>
    </pre>
  )
}

function MathRenderer({ element }: { element: MathElement }) {
  const content = element.displayMode ? `$$${element.content}$$` : element.content
  return (
    <div style={styleToCSS(element.style)} className="mb-3 text-center">
      <LatexText>{content}</LatexText>
    </div>
  )
}

function ImageRenderer({ element }: { element: ImageElement }) {
  return (
    <figure style={styleToCSS(element.style)} className="mb-3 text-center">
      <img
        src={element.content}
        alt={element.alt ?? element.caption ?? ''}
        style={element.width ? { width: element.width } : undefined}
        className="max-w-full rounded-lg mx-auto"
      />
      {element.caption && (
        <figcaption className="text-xs text-slate-400 mt-1">{element.caption}</figcaption>
      )}
    </figure>
  )
}

function VideoRenderer({ element }: { element: VideoElement }) {
  return (
    <figure style={styleToCSS(element.style)} className="mb-3">
      <video
        src={element.content}
        poster={element.poster}
        controls
        className="w-full rounded-lg"
      />
      {element.caption && (
        <figcaption className="text-xs text-slate-400 mt-1 text-center">{element.caption}</figcaption>
      )}
    </figure>
  )
}

function AudioRenderer({ element }: { element: AudioElement }) {
  return (
    <div style={styleToCSS(element.style)} className="mb-3">
      <audio src={element.content} controls className="w-full" />
      {element.caption && (
        <p className="text-xs text-slate-400 mt-1 text-center">{element.caption}</p>
      )}
    </div>
  )
}

function IframeRenderer({ element }: { element: IframeElement }) {
  return (
    <div style={styleToCSS(element.style)} className="mb-3 rounded-lg overflow-hidden border">
      <iframe
        src={element.content}
        title={element.title ?? '嵌入内容'}
        style={{ height: element.height ?? 400 }}
        className="w-full border-0"
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  )
}

function TableRenderer({ element }: { element: TableElement }) {
  return (
    <div
      style={styleToCSS(element.style)}
      className="mb-3 overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_th]:border [&_td]:px-3 [&_td]:py-2 [&_th]:px-3 [&_th]:py-2 [&_th]:bg-slate-100 text-sm"
      dangerouslySetInnerHTML={{ __html: element.content }}
    />
  )
}

function DividerRenderer({ element }: { element: DividerElement }) {
  return <hr style={styleToCSS(element.style)} className="my-4 border-slate-200" />
}

function SpacerRenderer({ element }: { element: SpacerElement }) {
  return <div style={{ height: element.height ?? 24, ...styleToCSS(element.style) }} />
}

function GeometryRenderer({ element }: { element: GeometryElement }) {
  return (
    <div
      style={styleToCSS(element.style)}
      className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500"
    >
      <div className="font-medium text-slate-700 mb-2">{element.title ?? '几何模型'}</div>
      <div style={{ height: element.height ?? 300 }} className="flex items-center justify-center">
        [几何图形: JSXGraph]
      </div>
    </div>
  )
}

function SvgExplainRenderer({ element, fragmentIndex = -1 }: { element: SvgExplainElement; fragmentIndex?: number }) {
  const scene = useMemo<SvgExplainScene | null>(() => {
    try {
      return JSON.parse(element.content) as SvgExplainScene
    } catch {
      return null
    }
  }, [element.content])

  // Fallback: 解析失败显示占位
  if (!scene || !scene.svg) {
    return (
      <div
        style={styleToCSS(element.style)}
        className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500"
      >
        <div className="font-medium text-slate-700 mb-2">{element.title ?? 'SVG 讲解'}</div>
        <div style={{ height: element.height ?? 300 }} className="flex items-center justify-center">
          [SVG 动画讲解]
        </div>
      </div>
    )
  }

  const steps = scene.steps ?? []
  const showAll = fragmentIndex < 0

  // 处理 SVG：根据 fragmentIndex 控制 layer 显隐和高亮
  const processedSvg = useMemo(() => {
    let svg = scene.svg
    // 为每个 data-step layer 添加可见性控制
    // data-step <= fragmentIndex 显示，data-step == fragmentIndex 高亮
    if (!showAll) {
      // 隐藏 step > fragmentIndex 的 layer
      svg = svg.replace(/<g([^>]*?)data-step=["'](\d+)["']([^>]*?)>/g, (match, pre, stepStr, post) => {
        const step = parseInt(stepStr, 10)
        if (step > fragmentIndex) {
          return `<g${pre}data-step="${stepStr}"${post} style="opacity:0;transition:opacity 0.4s">`
        }
        if (step === fragmentIndex) {
          return `<g${pre}data-step="${stepStr}"${post} class="svg-layer-active" style="transition:opacity 0.4s">`
        }
        return `<g${pre}data-step="${stepStr}"${post} style="opacity:1;transition:opacity 0.4s">`
      })
    }
    return svg
  }, [scene.svg, fragmentIndex, showAll])

  return (
    <div
      style={styleToCSS(element.style)}
      className="mb-3 flex gap-4 items-stretch w-full"
    >
      {/* 左侧 SVG 图形区域 */}
      <div className="w-1/2 flex flex-col rounded-lg border border-slate-200/60 bg-white/70 backdrop-blur-sm overflow-hidden shadow-sm">
        {scene.figureTitle && (
          <div className="px-3 py-2 text-xs font-medium text-slate-500 border-b border-slate-100 bg-slate-50/50">
            {scene.figureTitle}
          </div>
        )}
        <div
          className="flex-1 flex items-center justify-center p-3 [&_svg]:w-full [&_svg]:h-auto [&_svg]:max-h-[420px]"
          dangerouslySetInnerHTML={{ __html: processedSvg }}
        />
      </div>

      {/* 右侧文字解释区域 */}
      <div className="w-1/2 flex flex-col gap-2 overflow-y-auto pr-1">
        {steps.map((step, idx) => {
          const isVisible = showAll || idx <= fragmentIndex
          const isCurrent = !showAll && idx === fragmentIndex
          if (!isVisible) return null
          return (
            <div
              key={idx}
              className={`rounded-lg px-3 py-2 transition-all duration-300 ${
                isCurrent
                  ? 'bg-blue-50 border border-blue-200 shadow-sm'
                  : 'bg-white/50 border border-transparent'
              }`}
            >
              <div className={`text-sm font-semibold mb-1 ${
                isCurrent ? 'text-blue-700' : 'text-slate-600'
              }`}>
                {step.title}
              </div>
              <div className="text-sm leading-relaxed text-slate-700">
                <LatexText>{step.content}</LatexText>
              </div>
            </div>
          )
        })}
      </div>

      {/* 高亮层 CSS */}
      <style>{`
        .svg-layer-active [stroke] { stroke: var(--mc-accent, #2e86c1) !important; stroke-width: 3px !important; }
        .svg-layer-active [fill]:not([fill="none"]) { fill: var(--mc-accent, #2e86c1) !important; fill-opacity: 0.15; }
        .svg-layer-active text { fill: var(--mc-accent, #2e86c1) !important; font-weight: bold; }
      `}</style>
    </div>
  )
}

// ─── Shared Step-Explain Layout ───────────────────────────────────

/** 右侧文字步骤面板（svgExplain / jsxgraphExplain / geogebraExplain 共用） */
function StepTextPanel({ steps, fragmentIndex }: {
  steps: Array<{ title: string; content: string }>
  fragmentIndex: number
}) {
  const showAll = fragmentIndex < 0
  return (
    <div className="w-1/2 flex flex-col gap-2 overflow-y-auto pr-1">
      {steps.map((step, idx) => {
        const isVisible = showAll || idx <= fragmentIndex
        const isCurrent = !showAll && idx === fragmentIndex
        if (!isVisible) return null
        return (
          <div
            key={idx}
            className={`rounded-lg px-3 py-2 transition-all duration-300 ${
              isCurrent
                ? 'bg-blue-50 border border-blue-200 shadow-sm'
                : 'bg-white/50 border border-transparent'
            }`}
          >
            <div className={`text-sm font-semibold mb-1 ${
              isCurrent ? 'text-blue-700' : 'text-slate-600'
            }`}>
              {step.title}
            </div>
            <div className="text-sm leading-relaxed text-slate-700">
              <LatexText>{step.content}</LatexText>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** 左侧图形容器（带标题栏 + iframe） */
function FigurePanel({ figureTitle, srcDoc, height }: {
  figureTitle?: string
  srcDoc: string
  height?: number
}) {
  return (
    <div className="w-1/2 flex flex-col rounded-lg border border-slate-200/60 bg-white/70 backdrop-blur-sm overflow-hidden shadow-sm">
      {figureTitle && (
        <div className="px-3 py-2 text-xs font-medium text-slate-500 border-b border-slate-100 bg-slate-50/50">
          {figureTitle}
        </div>
      )}
      <iframe
        srcDoc={srcDoc}
        className="flex-1 w-full border-0"
        style={{ minHeight: height ?? 320 }}
        sandbox="allow-scripts allow-same-origin"
        title={figureTitle ?? '图形'}
      />
    </div>
  )
}

/** 构建 JSXGraph 分步 HTML：通过 postMessage 控制步骤显隐 */
function buildJsxgraphHtml(scene: JsxgraphExplainScene, fragmentIndex: number): string {
  const escapedCode = scene.code.replace(/<\//g, '<\\/')
  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/jsxgraph/distrib/jsxgraph.css">
<script src="https://cdn.jsdelivr.net/npm/jsxgraph/distrib/jsxgraphcore.js"><\/script>
<style>body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:transparent}.jxgbox{width:100%;height:100%}</style>
</head><body>
<div id="jxgbox" class="jxgbox"></div>
<script>
var board;
var stepObjects = {};
(function(){
${escapedCode}
})();
// 收集步骤对象：约定构造代码中用 registerStep(n, [names]) 注册
window.registerStep = function(n, names) { stepObjects[n] = names; };
function applyStep(idx) {
  if (!board) return;
  var all = board.getAllObjects();
  for (var key in all) {
    var obj = all[key];
    var step = obj._mcStep;
    if (step === undefined) continue;
    if (idx < 0) { obj.setAttribute({visible: true, strokeOpacity: 1}); }
    else if (step <= idx) {
      obj.setAttribute({visible: true});
      if (step === idx) { obj.setAttribute({strokeColor: '#2e86c1', strokeWidth: 3, strokeOpacity: 1}); }
      else { obj.setAttribute({strokeOpacity: 0.85}); }
    } else { obj.setAttribute({visible: false}); }
  }
}
window.addEventListener('message', function(e) {
  if (e.data && e.data.type === 'mc-step') applyStep(e.data.step);
});
applyStep(${fragmentIndex});
<\/script></body></html>`
}

/** 构建 GeoGebra 分步 HTML：按步骤执行命令组 */
function buildGeogebraHtml(scene: GeogebraExplainScene, fragmentIndex: number): string {
  // 解析命令分组："## Step N" 注释行分隔
  const groups: string[][] = []
  let current: string[] = []
  for (const line of scene.commands.split('\n')) {
    if (line.trim().startsWith('## Step') || line.trim().startsWith('## step')) {
      if (current.length > 0) groups.push(current)
      current = []
    } else if (line.trim()) {
      current.push(line.trim())
    }
  }
  if (current.length > 0) groups.push(current)

  const groupsJson = JSON.stringify(groups).replace(/<\//g, '<\\/')
  const viewJson = scene.view ? JSON.stringify(scene.view) : 'null'
  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8">
<script src="https://www.geogebra.org/apps/deployggb.js"><\/script>
<style>body{margin:0;height:100vh;overflow:hidden}#ggb-applet{width:100%;height:100%}</style>
</head><body>
<div id="ggb-applet"></div>
<script>
var groups = ${groupsJson};
var view = ${viewJson};
var executed = -1;
var api = null;
function executeUpTo(idx) {
  if (!api) return;
  for (var g = executed + 1; g <= idx && g < groups.length; g++) {
    groups[g].forEach(function(cmd) { api.evalCommand(cmd); });
  }
  if (idx > executed) executed = idx;
}
var container = document.getElementById('ggb-applet');
var params = {
  appName: 'geometry', width: container.clientWidth || 800, height: container.clientHeight || 600,
  showToolBar: false, showAlgebraInput: false, showMenuBar: false,
  enableRightClick: false, showZoomButtons: false, preventFocus: true,
  appletOnLoad: function(a) {
    api = a;
    if (view) { api.setCoordSystem(view[0], view[1], view[2], view[3]); }
    window.addEventListener('message', function(e) {
      if (e.data && e.data.type === 'mc-step') executeUpTo(e.data.step);
    });
    executeUpTo(${fragmentIndex});
  }
};
new GGBApplet(params, true).inject('ggb-applet');
<\/script></body></html>`
}

// ─── JSXGraph Explain Renderer ────────────────────────────────────

function JsxgraphExplainRenderer({ element, fragmentIndex = -1 }: { element: JsxgraphExplainElement; fragmentIndex?: number }) {
  const scene = useMemo<JsxgraphExplainScene | null>(() => {
    try {
      return JSON.parse(element.content) as JsxgraphExplainScene
    } catch {
      return null
    }
  }, [element.content])

  if (!scene || !scene.code) {
    return (
      <div style={styleToCSS(element.style)} className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
        <div className="font-medium text-slate-700 mb-2">{element.title ?? 'JSXGraph 讲解'}</div>
        <div style={{ height: element.height ?? 300 }} className="flex items-center justify-center">[JSXGraph 动画讲解]</div>
      </div>
    )
  }

  const srcDoc = useMemo(
    () => buildJsxgraphHtml(scene, fragmentIndex),
    [scene, fragmentIndex]
  )

  return (
    <div style={styleToCSS(element.style)} className="mb-3 flex gap-4 items-stretch w-full">
      <FigurePanel figureTitle={scene.figureTitle} srcDoc={srcDoc} height={element.height} />
      <StepTextPanel steps={scene.steps ?? []} fragmentIndex={fragmentIndex} />
    </div>
  )
}

// ─── GeoGebra Explain Renderer ────────────────────────────────────

function GeogebraExplainRenderer({ element, fragmentIndex = -1 }: { element: GeogebraExplainElement; fragmentIndex?: number }) {
  const scene = useMemo<GeogebraExplainScene | null>(() => {
    try {
      return JSON.parse(element.content) as GeogebraExplainScene
    } catch {
      return null
    }
  }, [element.content])

  if (!scene || !scene.commands) {
    return (
      <div style={styleToCSS(element.style)} className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
        <div className="font-medium text-slate-700 mb-2">{element.title ?? 'GeoGebra 讲解'}</div>
        <div style={{ height: element.height ?? 300 }} className="flex items-center justify-center">[GeoGebra 动画讲解]</div>
      </div>
    )
  }

  const srcDoc = useMemo(
    () => buildGeogebraHtml(scene, fragmentIndex),
    [scene, fragmentIndex]
  )

  return (
    <div style={styleToCSS(element.style)} className="mb-3 flex gap-4 items-stretch w-full">
      <FigurePanel figureTitle={scene.figureTitle} srcDoc={srcDoc} height={element.height} />
      <StepTextPanel steps={scene.steps ?? []} fragmentIndex={fragmentIndex} />
    </div>
  )
}

// ─── Quiz Renderer ─────────────────────────────────────────────────

/** quiz 提交结果（用于同步错题本） */
export interface QuizSubmitResult {
  elementId: string
  quiz: QuizContent
  /** 与题库 answer 格式一致：{ value: 'A' | 'AB' | '对' | 填空文本 } */
  userAnswer: { value: string }
  isCorrect: boolean
}

const QUIZ_TYPE_LABELS: Record<QuizContent['type'], string> = {
  choice: '单选题',
  multi_choice: '多选题',
  true_false: '判断题',
  fill_blank: '填空题',
}

/** 判断作答是否正确（纯函数，提交与展示共用；答案格式与题库 answer.value 一致） */
function checkQuizAnswer(quiz: QuizContent, value: string): boolean {
  const correct = quiz.answer?.value ?? ''
  switch (quiz.type) {
    case 'choice':
    case 'true_false':
      return value.trim() === correct.trim()
    case 'multi_choice':
      return [...value].sort().join('') === [...correct].sort().join('')
    case 'fill_blank': {
      const answers = correct.split('|').map((a) => a.trim().toLowerCase())
      return answers.includes(value.trim().toLowerCase())
    }
    default:
      return false
  }
}

function QuizRenderer({ element, onQuizResult }: { element: QuizElement; onQuizResult?: (result: QuizSubmitResult) => void }) {
  // 存量课件可能仍是旧结构，统一归一化为题库对齐结构
  const quiz = useMemo(() => normalizeQuizContent(element.content), [element.content])
  return (
    <div style={styleToCSS(element.style)} className="mb-3 rounded-lg border border-blue-200 bg-blue-50/50 p-4">
      <div className="text-xs font-medium text-blue-600 mb-2">
        {QUIZ_TYPE_LABELS[quiz.type] ?? quiz.type}
      </div>
      <QuizInteractive quiz={quiz} elementId={element.id} onQuizResult={onQuizResult} />
    </div>
  )
}

function QuizInteractive({ quiz, elementId, onQuizResult }: {
  quiz: QuizContent
  elementId: string
  onQuizResult?: (result: QuizSubmitResult) => void
}) {
  /** 已选选项 label（选择题）或 对/错（判断题） */
  const [selected, setSelected] = useState<string[]>([])
  const [submitted, setSubmitted] = useState(false)
  const [fillAnswer, setFillAnswer] = useState('')

  const userValue = useMemo(() => {
    if (quiz.type === 'fill_blank') return fillAnswer
    if (quiz.type === 'multi_choice') return [...selected].sort().join('')
    return selected[0] ?? ''
  }, [quiz.type, selected, fillAnswer])

  const isCorrect = useMemo(
    () => (submitted ? checkQuizAnswer(quiz, userValue) : false),
    [submitted, userValue, quiz]
  )

  const handleOptionClick = (val: string) => {
    if (submitted) return
    if (quiz.type === 'multi_choice') {
      setSelected((prev) =>
        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
      )
    } else {
      setSelected([val])
    }
  }

  const handleSubmit = () => {
    if (quiz.type === 'fill_blank' && !fillAnswer.trim()) return
    if (quiz.type !== 'fill_blank' && selected.length === 0) return
    setSubmitted(true)
    // 上报答题结果（错题同步由上层处理）
    const correct = checkQuizAnswer(quiz, userValue)
    onQuizResult?.({ elementId, quiz, userAnswer: { value: userValue }, isCorrect: correct })
  }

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-slate-800">
        <LatexText>{quiz.stem}</LatexText>
      </div>

      {quiz.images?.map((src, i) => (
        <img key={i} src={src} alt="" className="max-w-full rounded" />
      ))}

      {/* Choice options */}
      {(quiz.type === 'choice' || quiz.type === 'multi_choice') && quiz.options && (
        <div className="space-y-2">
          {quiz.options.map((opt) => {
            const isSelected = selected.includes(opt.label)
            const isCorrectOpt = submitted && (quiz.answer?.value ?? '').includes(opt.label)
            const isWrongSelected = submitted && isSelected && !isCorrectOpt

            return (
              <button
                key={opt.label}
                onClick={() => handleOptionClick(opt.label)}
                disabled={submitted}
                className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                  isCorrectOpt
                    ? 'border-green-400 bg-green-50 text-green-800'
                    : isWrongSelected
                    ? 'border-red-400 bg-red-50 text-red-800'
                    : isSelected
                    ? 'border-blue-400 bg-blue-50 text-blue-800'
                    : 'border-slate-200 bg-white hover:border-blue-300'
                }`}
              >
                <span className="font-medium mr-1">{opt.label}.</span>
                {opt.content}
              </button>
            )
          })}
        </div>
      )}

      {/* True/False */}
      {quiz.type === 'true_false' && (
        <div className="flex gap-3">
          {['对', '错'].map((val) => {
            const isSelected = selected[0] === val
            const isCorrectOpt = submitted && val === (quiz.answer?.value ?? '')
            const isWrongSelected = submitted && isSelected && !isCorrectOpt

            return (
              <button
                key={val}
                onClick={() => handleOptionClick(val)}
                disabled={submitted}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  isCorrectOpt
                    ? 'border-green-400 bg-green-50 text-green-800'
                    : isWrongSelected
                    ? 'border-red-400 bg-red-50 text-red-800'
                    : isSelected
                    ? 'border-blue-400 bg-blue-50 text-blue-800'
                    : 'border-slate-200 bg-white hover:border-blue-300'
                }`}
              >
                {val === '对' ? '正确 ✓' : '错误 ✗'}
              </button>
            )
          })}
        </div>
      )}

      {/* Fill blank */}
      {quiz.type === 'fill_blank' && (
        <div>
          <input
            type="text"
            value={fillAnswer}
            onChange={(e) => setFillAnswer(e.target.value)}
            disabled={submitted}
            placeholder="输入答案..."
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      )}

      {/* Submit button */}
      {!submitted && (
        <button
          onClick={handleSubmit}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          提交答案
        </button>
      )}

      {/* Feedback */}
      {submitted && (
        <div className={`text-sm px-3 py-2 rounded-lg ${isCorrect ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {isCorrect ? '✓ 回答正确！' : '✗ 回答错误'}
          {quiz.analysis && (
            <p className="mt-1 text-xs opacity-80">{quiz.analysis}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main Element Renderer ─────────────────────────────────────────

export interface ElementRendererProps {
  element: MicroElement
  /** 当前 fragment index（用于逐步高亮），-1 表示全部显示 */
  fragmentIndex?: number
  /** 该 element 对应的 fragment index */
  elementFragmentIndex?: number
  /** 当前 fragment 累计应显示的元素数量（优先于 elementFragmentIndex 的门控语义） */
  elementVisibleCount?: number
  /** 是否处于编辑模式（白板等可交互元素可编辑） */
  editable?: boolean
  /** 元素内容变更回调（编辑模式下，返回更新后的完整 element） */
  onElementChange?: (element: MicroElement) => void
  /** quiz 提交回调（用于错题同步） */
  onQuizResult?: (result: QuizSubmitResult) => void
}

export function ElementRenderer({ element, fragmentIndex = -1, elementFragmentIndex, elementVisibleCount, editable = false, onElementChange, onQuizResult }: ElementRendererProps) {
  // 分步讲解元素始终可见：其内部按 fragmentIndex 控制 data-step / _mcStep / 命令分组的显隐
  const isExplainElement =
    element.type === 'svgExplain' ||
    element.type === 'jsxgraphExplain' ||
    element.type === 'geogebraExplain'

  // Fragment visibility：优先按 elementVisibleCount 门控（与 TTS 朗读内容同步），
  // 否则退化为 elementFragmentIndex <= fragmentIndex
  const isVisible =
    isExplainElement ||
    fragmentIndex < 0 ||
    (elementVisibleCount !== undefined
      ? elementFragmentIndex === undefined || elementFragmentIndex < elementVisibleCount
      : elementFragmentIndex === undefined || elementFragmentIndex <= fragmentIndex)

  if (!isVisible) return null

  switch (element.type) {
    case 'heading':
      return <HeadingRenderer element={element} />
    case 'paragraph':
      return <ParagraphRenderer element={element} />
    case 'quote':
      return <QuoteRenderer element={element} />
    case 'list':
      return <ListRenderer element={element} />
    case 'code':
      return <CodeRenderer element={element} />
    case 'math':
      return <MathRenderer element={element} />
    case 'image':
      return <ImageRenderer element={element} />
    case 'video':
      return <VideoRenderer element={element} />
    case 'audio':
      return <AudioRenderer element={element} />
    case 'iframe':
      return <IframeRenderer element={element} />
    case 'table':
      return <TableRenderer element={element} />
    case 'divider':
      return <DividerRenderer element={element} />
    case 'spacer':
      return <SpacerRenderer element={element} />
    case 'geometry':
      return <GeometryRenderer element={element} />
    case 'svgExplain':
      return <SvgExplainRenderer element={element} fragmentIndex={fragmentIndex} />
    case 'jsxgraphExplain':
      return <JsxgraphExplainRenderer element={element} fragmentIndex={fragmentIndex} />
    case 'geogebraExplain':
      return <GeogebraExplainRenderer element={element} fragmentIndex={fragmentIndex} />
    case 'quiz':
      return <QuizRenderer element={element} onQuizResult={onQuizResult} />
    case 'excalidraw':
      return (
        <ExcalidrawRenderer
          element={element}
          editable={editable}
          onSceneChange={onElementChange ? (sceneJson) => onElementChange({ ...element, content: sceneJson }) : undefined}
        />
      )
    default:
      return null
  }
}
